import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Megaphone, CheckCircle, ArrowLeft, Rocket } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import Header from "@/components/Header";

export default function Promote() {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("sponsor_24h");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [vRes, pRes] = await Promise.all([
      fetch(`${API}/videos/${videoId}`, { credentials: "include" }),
      fetch(`${API}/sponsored/packages`),
    ]);
    if (vRes.ok) setVideo(await vRes.json());
    if (pRes.ok) setPackages((await pRes.json()).packages);
  }, [videoId]);

  useEffect(() => { load(); }, [load]);

  const handleBuy = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/sponsored/create-session`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ video_id: videoId, package_id: selected, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      const data = await res.json();
      window.location.href = data.url;
    } catch (e) { toast.error(e.message); setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6 max-w-2xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#FF66FF] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="text-center mb-6">
          <Megaphone className="w-14 h-14 mx-auto text-[#FF6600] mb-3" style={{ filter: "drop-shadow(0 0 15px #FF6600)" }} />
          <h1 className="text-3xl font-bold text-[#FF6600]" style={{ textShadow: "0 0 10px #FF6600" }} data-testid="promote-title">Promote Your Video</h1>
          <p className="text-[#FF66FF] mt-1">Get featured at the top of the Video Feed</p>
        </div>

        {video && (
          <div className="myspace-card mb-6">
            <p className="text-xs text-[#99CCFF] uppercase mb-1">Promoting</p>
            <p className="text-[#00FFFF] font-bold">{video.caption || "(no caption)"}</p>
            <p className="text-xs text-[#FF66FF] mt-1">{video.views || 0} views · {video.likes?.length || 0} likes</p>
          </div>
        )}

        <div className="space-y-3 mb-6">
          {packages.map(pkg => (
            <button key={pkg.id} onClick={() => setSelected(pkg.id)}
              className={`w-full p-4 border-2 text-left transition-all ${selected === pkg.id ? "border-[#FF6600] bg-[rgba(255,102,0,0.1)]" : "border-[#6600CC] hover:border-[#FF00FF]"}`}
              data-testid={`promote-package-${pkg.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-[#00FFFF]">{pkg.label}</p>
                  <p className="text-sm text-[#99CCFF]">${pkg.amount} · {pkg.hours}h in feed</p>
                </div>
                {selected === pkg.id && <CheckCircle className="w-6 h-6 text-[#FF6600]" />}
              </div>
            </button>
          ))}
        </div>

        <button onClick={handleBuy} disabled={loading || !video} className="myspace-btn-primary w-full text-lg py-3 disabled:opacity-50" data-testid="promote-buy-btn">
          {loading ? "Redirecting..." : `Sponsor — $${packages.find(p => p.id === selected)?.amount || ""}`}
        </button>
      </main>
    </div>
  );
}

export function PromoteSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  const poll = useCallback(async (sid, attempt) => {
    if (attempt >= 6) { setStatus("timeout"); return; }
    try {
      const r = await fetch(`${API}/sponsored/status/${sid}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        if (d.payment_status === "paid") { setStatus("paid"); return; }
        if (d.status === "expired") { setStatus("expired"); return; }
      }
    } catch (e) { /* retry */ }
    setTimeout(() => poll(sid, attempt + 1), 2000);
  }, []);

  useEffect(() => { if (sessionId) poll(sessionId, 0); }, [sessionId, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <div className="myspace-card text-center max-w-md w-full">
        {status === "checking" && (<><div className="w-12 h-12 border-4 border-[#FF6600] border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-[#FF6600]">Activating sponsorship...</p></>)}
        {status === "paid" && (<>
          <Rocket className="w-20 h-20 mx-auto text-[#FF6600] mb-4" style={{ filter: "drop-shadow(0 0 20px #FF6600)" }} />
          <h2 className="text-3xl font-bold text-[#FF6600] mb-2" style={{ textShadow: "0 0 15px #FF6600" }} data-testid="promote-success-title">Sponsorship Live!</h2>
          <p className="text-[#00FFFF] mb-6">Your video is now featured at the top of the feed.</p>
          <button onClick={() => navigate("/videos")} className="myspace-btn-primary" data-testid="promote-success-continue-btn">View Feed</button>
        </>)}
        {(status === "timeout" || status === "expired") && (<><p className="text-[#FF6600] text-lg font-bold mb-4">Payment could not be verified</p><button onClick={() => navigate("/videos")} className="myspace-btn">Back</button></>)}
      </div>
    </div>
  );
}
